/**
 * formatApiError(err) — turn ANY thrown error / axios error / fetch error /
 * FastAPI {detail} / Pydantic validation array into a plain readable string.
 *
 * Safe to use directly inside JSX as a child, inside toast.error(),
 * and inside new Error(formatApiError(...)) so React never receives raw objects.
 *
 * Rules (in order):
 *  1. If null/undefined → "Something went wrong."
 *  2. If string → return as-is.
 *  3. If axios-like: read err.response.data → recurse.
 *  4. If object with `detail`:
 *     - detail is string → return it
 *     - detail is array (Pydantic) → join the `msg` of each item
 *     - detail is object → recurse on it
 *  5. If has `message`/`msg`/`error` string fields → return that string.
 *  6. Fallback: JSON.stringify (truncated).
 */
export function formatApiError(err, fallback = "Something went wrong.") {
  // DEV-ONLY safety beacon: helps trace any caller that still hands us a raw
  // FastAPI/Pydantic object so we can route it through formatApiError too.
  if (typeof err === "object" && err !== null && !err.response && (Array.isArray(err.detail) || (err.type && err.loc && err.msg))) {
    try {
      // eslint-disable-next-line no-console
      console.trace("[formatApiError] received raw Pydantic-shaped payload:", err);
    } catch {
      /* ignore */
    }
  }
  const out = _formatApiErrorImpl(err, fallback);
  // Final hard guarantee: we ALWAYS return a string. If anyone bypasses us
  // and somehow puts a non-string into state, this also makes JSX-safe.
  return typeof out === "string" ? out : String(out ?? fallback);
}

function _formatApiErrorImpl(err, fallback) {
  if (err === null || err === undefined) return fallback;
  if (typeof err === "string") return err;

  // Axios error: err.response.data is what we actually want.
  if (err && err.response && err.response.data !== undefined) {
    return _formatApiErrorImpl(err.response.data, fallback);
  }

  if (typeof err === "object") {
    // FastAPI shape: { detail: ... }
    if ("detail" in err) {
      const d = err.detail;
      if (typeof d === "string") return d;
      if (Array.isArray(d)) {
        const parts = d
          .map((item) => {
            if (typeof item === "string") return item;
            if (item && typeof item === "object") {
              if (typeof item.msg === "string") {
                if (Array.isArray(item.loc) && item.loc.length > 0) {
                  return `${item.loc.join(".")}: ${item.msg}`;
                }
                return item.msg;
              }
              if (typeof item.message === "string") return item.message;
            }
            return null;
          })
          .filter(Boolean);
        if (parts.length) return parts.join("; ");
      }
      if (d && typeof d === "object") return _formatApiErrorImpl(d, fallback);
    }
    if (typeof err.message === "string" && err.message) return err.message;
    if (typeof err.msg === "string" && err.msg) return err.msg;
    if (typeof err.error === "string" && err.error) return err.error;

    // Direct Pydantic item: {type, loc, msg, input, ctx}
    if (typeof err.msg === "string") return err.msg;

    // Last resort — serialize but cap length so toasts/banners stay readable.
    try {
      const s = JSON.stringify(err);
      if (s && s !== "{}") return s.length > 240 ? `${s.slice(0, 240)}…` : s;
    } catch {
      /* ignore */
    }
  }

  return fallback;
}
